import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { usePropertyField, RichTextContent } from '@vibesites/runtime/react';

// MarketApts embeds the fee guide in an iframe that auto-resizes its height via
// their hosted iframe-resizer script. The per-property AIM code selects which
// community's fees to show (the `?code=` query param).
const IFRAME_RESIZER_SRC = 'https://www.marketapts.com/js/iframeResizer.min.js';

// Fallback used when a property has not been assigned an AIM code yet.
const DEFAULT_AIM_CODE = '1080WMRK';

declare global {
  interface Window {
    iFrameResize?: (options: Record<string, unknown>, target: string | HTMLElement) => void;
  }
}

export function FeeGuide() {
  // The AIM property code is per-property data, not editable body copy — it
  // maps to the `code` query param in the MarketApts embed URL.
  const aimCode = (usePropertyField('custom_aim_property_code') as string | null) || DEFAULT_AIM_CODE;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const el = iframeRef.current;
    if (!el || typeof window === 'undefined') return;

    const applyResize = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({ log: false }, iframeRef.current);
      }
    };

    // Script already loaded (e.g. another instance on the page) — just init.
    if (window.iFrameResize) {
      applyResize();
      return;
    }

    // Otherwise load the MarketApts resizer once, then initialize on load.
    let script = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_RESIZER_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = IFRAME_RESIZER_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', applyResize);
    return () => {
      script?.removeEventListener('load', applyResize);
    };
  }, [aimCode]);

  const iframeSrc = `https://www.marketapts.com/iframes/fee-guide?code=${encodeURIComponent(aimCode)}`;

  return (
    <section className="bg-white py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="feeguide.heading"
          as="h2"
          className="font-heading text-2xl md:text-3xl text-[var(--vibe-color-text)] mb-6 text-center"
        />

        <div className="row container">
          <iframe
            ref={iframeRef}
            id="fee-guide"
            title="Fee Guide"
            src={iframeSrc}
            style={{ border: 'none', width: '1px', minWidth: '100%' }}
          />
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeGuide',
  displayName: 'Fee Guide',
  tokens: {
    'feeguide.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Fee Guide</p>',
    },
  },
};

(FeeGuide as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeGuide;
